import type {User} from "../../../data/models/User";
import type {UIVersion} from "../../../data/state/AppState";
import {
	GetOrganizationWideNavigationMenus,
	GetPortfolioWideNavigationMenus,
	NavigationEnum,
	PermissionSetSubNav,
	SettingsModuleNav,
	SettingsModuleSubNav,
	SettingsPermissionsSubNav,
	SettingsSubNav,
} from "../../../Enums";

interface IHashPattern {
	menu: NavigationEnum;
	pattern: RegExp;
}

export class StringUtils {
	// TODO it could be better to convert the ids when loading the models, then it only needs to be done once?
	// "f12" -> 12
	public static refId2Number(refId: string): string | number {
		if (refId?.substring) {
			return Number(refId.substring(1));
		}
		return refId;
	}

	// Converts "DashedLine" to "Dashed Line"
	public static pascalToSpacedName(valueInPascalCase: string): string {
		return (
			valueInPascalCase
				// Add a space before each uppercase letter except the first one
				.replace(/([A-Z])/g, " $1")
				// Remove any leading space that might occur
				.trim()
		);
	}

	public static readonly xyiconOrBoundaryRefIdPattern = new RegExp(`^(x\\d+|b\\d+)$`);

	public static readonly uuidPattern = new RegExp(`[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}`);

	private static readonly spaceBasePattern = new RegExp(`^#?app/${this.uuidPattern.source}/p[0-9]+/space/`);

	private static readonly spacePattern = new RegExp(`${this.spaceBasePattern.source}(s\\d+)$`);

	public static getHashBasePatterns(currentUIVersion: UIVersion): IHashPattern[] {
		const uuid = this.uuidPattern.source;

		return [
			...GetOrganizationWideNavigationMenus(currentUIVersion).map((menu: NavigationEnum) => ({
				menu,
				pattern: new RegExp(`^app/${uuid}/${menu}($|/)`),
			})),
			...GetPortfolioWideNavigationMenus().map((menu: NavigationEnum) => ({
				menu,
				pattern: new RegExp(`^app/${uuid}/p[0-9]+/${menu}($|/)`),
			})),
		];
	}

	public static isHashValidForSpaceBase(hash: string): boolean {
		return this.spaceBasePattern.test(hash);
	}

	public static isHashValidForSpace(hash: string): boolean {
		return this.spacePattern.test(hash);
	}

	public static getSpaceRefIdFromHash(hash: string): string {
		const match = hash.match(this.spacePattern);

		if (match) {
			return match[1];
		}

		return "";
	}

	public static cleanHash(hash: string): string {
		// Remove leading "#"
		return hash.startsWith("#") ? hash.slice(1) : hash;
	}

	public static isHashValid(hash: string, currentUIVersion: UIVersion, user: User): boolean {
		const isAdmin = user?.isAdmin;

		// General structure must start with: app/<orgId>/...
		// UUID v4 regex
		const uuid = this.uuidPattern.source;

		//
		// 1) Organization-level modules
		// app/33261f51-672a-423f-95b2-fceba9bf9175/catalog
		//
		const orgModules = [NavigationEnum.PORTFOLIOS, NavigationEnum.CATALOG, NavigationEnum.REPORTS];
		if (currentUIVersion === "5.0") {
			orgModules.push(NavigationEnum.HOME);
		}
		const orgRoute = new RegExp(`^app/${uuid}/(${orgModules.join("|")})$`);

		//
		// 2) Settings routes
		// settings/personal
		// settings/catalog/types
		//
		const nonAdminSettingsRoute = new RegExp(`^app/${uuid}/${NavigationEnum.SETTINGS}/(${SettingsSubNav.PERSONAL}|${SettingsSubNav.ORGANIZATION})$`);
		const settingsRoutes = [nonAdminSettingsRoute];

		if (isAdmin) {
			const moduleBasePattern = `^app/${uuid}/${NavigationEnum.SETTINGS}/${SettingsSubNav.MODULES}/(${Object.values(SettingsModuleNav).join("|")})`;

			settingsRoutes.push(
				new RegExp(`^app/${uuid}/${NavigationEnum.SETTINGS}/(${SettingsSubNav.PERSONAL}|${SettingsSubNav.ORGANIZATION})$`),
				new RegExp(
					`^app/${uuid}/${NavigationEnum.SETTINGS}/${SettingsSubNav.PERMISSIONS}/(${SettingsPermissionsSubNav.USERS}|${SettingsPermissionsSubNav.USER_GROUPS}|${SettingsPermissionsSubNav.PORTFOLIO_GROUPS})$`,
				),
				new RegExp(
					`^app/${uuid}/${NavigationEnum.SETTINGS}/${SettingsSubNav.PERMISSIONS}/${SettingsPermissionsSubNav.PERMISSION_SETS}/(${Object.values(PermissionSetSubNav).join("|")})$`,
				),
				new RegExp(`${moduleBasePattern}/(${SettingsModuleSubNav.TYPES}|${SettingsModuleSubNav.FIELDS}|${SettingsModuleSubNav.LAYOUTS})$`),
			);

			if (currentUIVersion === "4.0") {
				const adminSettingsRoute4 = new RegExp(`${moduleBasePattern}/${SettingsModuleSubNav.NAVIGATION}$`);
				settingsRoutes.push(adminSettingsRoute4);
			}
		}

		//
		// 3) Portfolio-level modules
		// p5/xyicons
		//
		const portfolioModules = [NavigationEnum.XYICONS, NavigationEnum.BOUNDARIES, NavigationEnum.SPACES, NavigationEnum.REPORT_VIEWS].join("|");
		const portfolioRoute = new RegExp(`^app/${uuid}/p[0-9]+/(${portfolioModules})$`);

		//
		// 4) Space-level routes
		// p5/spaces
		//
		const spacesRoute = new RegExp(`^app/${uuid}/p[0-9]+/spaces$`);

		//
		// 5) Portfolio-level details routes
		// p5/details/xyicon/x32
		// p5/details/boundary/b52
		//
		const detailsRoute = new RegExp(`^app/${uuid}/p[0-9]+/${NavigationEnum.DETAILS}/(xyicon/x[0-9]+|boundary/b[0-9]+)$`);

		// Combine all tests
		const tests = [orgRoute, ...settingsRoutes, portfolioRoute, spacesRoute, detailsRoute, this.spacePattern];

		if (currentUIVersion === "4.0") {
			const orgRouteWithActivePortfolio = new RegExp(`^app/${uuid}/${NavigationEnum.PORTFOLIOS}/p[0-9]+$`);
			tests.push(orgRouteWithActivePortfolio);
		}

		// As per #12052, we should be able to sign up a new user while another one is logged in...
		tests.push(new RegExp(`^auth/new_user\\?tokenid=`));

		if (!user) {
			// 6) Routes that can be accessed only while logged out
			//
			tests.push(
				new RegExp(`^auth/login($|/)`),
				new RegExp(`^auth/login/sso($|/)`),
				new RegExp(`^auth/signin($|/)`),
				new RegExp(`^auth/forgotten_password`),
				new RegExp(`^auth/sso-login-failed`),
				new RegExp(`^auth/ssocallback`),
			);
		}

		const h = this.cleanHash(hash);

		return tests.some((t) => t.test(h));
	}

	public static decomposeParts(pattern: string): {value: string; isParam: boolean; optional: boolean}[] {
		const parts = pattern.split("/");
		const result = [];

		for (const part of parts) {
			const {result: paramValue, removed: isParam} = StringUtils.removeCharacter(part, ":");
			const {result: value, removed: optional} = StringUtils.removeCharacter(paramValue, "?");

			result.push({
				value: value,
				isParam: isParam,
				optional: optional,
			});
		}

		return result;
	}

	private static removeCharacter(value: string, char: string) {
		const result = value.split(char).join("");

		return {
			result: result,
			removed: result.length !== value.length,
		};
	}

	public static trim(value: string) {
		value = value || "";
		if (value.trim) {
			return value.trim();
		}
		return value;
	}

	public static leftPad(value: number | string, width: number, char = "0"): string {
		const n: string = `${value}`;

		return n.length >= width ? n : new Array(width - n.length + 1).join(char) + n;
	}

	// https://stackoverflow.com/questions/3410464/how-to-find-indices-of-all-occurrences-of-one-string-in-another-in-javascript
	public static getIndicesOfSubstring(source: string, find: string) {
		if (!source) {
			return [];
		}
		// if find is empty string return all indexes.
		if (!find) {
			// or shorter arrow function:
			return source.split("").map((_, i) => i);
		}
		const result = [];

		for (let i = 0; i < source.length; ++i) {
			// If you want to search case insensitive use
			if (source.substring(i, i + find.length).toLowerCase() === find) {
				result.push(i);
			}
		}

		return result;
	}

	public static equalsIgnoreCase(a: string, b: string) {
		a = a || "";
		b = b || "";
		if (!a.toLowerCase) {
			return false;
		}
		if (!b.toLowerCase) {
			return false;
		}

		a = a.toLowerCase();
		b = b.toLowerCase();

		return a === b;
	}

	public static containsIgnoreCase(source: string, search: string) {
		source = source || "";
		search = search || "";
		if (!source.toLowerCase) {
			return false;
		}
		if (!search.toLowerCase) {
			return false;
		}

		source = source.toLowerCase();
		search = search.toLowerCase();

		return source.includes(search);
	}

	public static sortIgnoreCase(a: string, b: string): 0 | 1 | -1 {
		a = a || "";
		b = b || "";
		if (!a.toLowerCase) {
			return 0;
		}
		if (!b.toLowerCase) {
			return 0;
		}

		a = a.toLowerCase();
		b = b.toLowerCase();

		return a < b ? -1 : 1;
	}

	public static regexHighlight(string: string, queryStr: string): string {
		if (!string) {
			return string;
		} else if (queryStr) {
			// https://stackoverflow.com/questions/7313395/case-insensitive-replace-all
			const esc = queryStr.replace(/[-\/\\^$*+?.()|[\]{}]/g, "\\$&");
			const reg = new RegExp(esc, "ig");
			return string.replace(reg, (str: string) => (str ? `<b>${str}</b>` : ""));
		} else {
			return string;
		}
	}

	public static emailValidator(email: string): boolean {
		if (typeof email !== "string") {
			return false;
		}
		// http://emailregex.com/
		return !!email.match(
			/^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/,
		);
	}

	public static isBlank(value: any): boolean {
		if (value === null) {
			return true;
		}
		if (value === undefined) {
			return true;
		}
		if (value === false) {
			return false;
		} // false is not a blank value!

		if (StringUtils.trim(value) === "") {
			return true;
		}

		return false;
	}

	public static filterByKeywords(list: {keywords: string[]}[], searchValue: string) {
		return list.filter((element: {keywords: string[]}) => {
			for (const keyword of element.keywords) {
				if (StringUtils.containsIgnoreCase(keyword, searchValue)) {
					return true;
				}
			}

			return false;
		});
	}

	public static capitalize(value: string = "") {
		return value.charAt(0).toUpperCase() + value.slice(1);
	}

	public static decapitalize(value: string = "") {
		return value.charAt(0).toLowerCase() + value.slice(1);
	}

	public static lowerCase(value: string) {
		return value?.toLowerCase ? value.toLowerCase() : value;
	}

	public static titleCase(value: string = "") {
		return value.charAt(0).toUpperCase() + value.slice(1).toLowerCase();
	}

	public static isTruthy(value: any): boolean {
		return value || value === 0 || value === false;
	}

	public static formatNumberWithCommas(value: number): string {
		const valueString = value.toString();

		if (valueString.includes("e")) {
			return valueString;
		}

		const decimalIndex = valueString.indexOf(".");
		const integerPart = decimalIndex === -1 ? valueString : valueString.slice(0, decimalIndex);
		const decimalPart = decimalIndex === -1 ? "" : valueString.slice(decimalIndex);
		const sign = integerPart.startsWith("-") ? "-" : "";
		let remainingDigits = sign ? integerPart.slice(1) : integerPart;
		const digitGroups: string[] = [];

		while (remainingDigits.length > 3) {
			digitGroups.unshift(remainingDigits.slice(-3));
			remainingDigits = remainingDigits.slice(0, -3);
		}
		digitGroups.unshift(remainingDigits);

		return `${sign}${digitGroups.join(",")}${decimalPart}`;
	}

	public static formatPastedText(pastedText: string, pastedHTML: string) {
		pastedText = StringUtils.formatTableString(pastedText);

		pastedText = pastedText
			.split("\n")
			.map((line) => StringUtils.removeTrailingCharacter(line, "\t"))
			.join("\n");

		// Add GUIDs
		const guids: string[] = [];
		const itemIdString = 'item-id="';
		const guidLength = 36; // a GUID is 36 characters long
		const indicesOfGUIDs = StringUtils.getIndicesOfSubstring(pastedHTML, itemIdString);

		for (const index of indicesOfGUIDs) {
			const guid = pastedHTML.substring(index + itemIdString.length, index + itemIdString.length + guidLength);

			guids.push(guid);
		}

		pastedText = `GUID\t${pastedText}`;
		pastedText = pastedText.match(/\n\n\n/g) ? pastedText.replace(/\n\n\n/g, "\n\nGUID\t") : pastedText.replace(/\n\n/g, "\n\nGUID\t");

		let guidIndex = 0;

		for (let i = 0; i < pastedText.length - 1; ++i) {
			if (pastedText[i - 1] !== "\n" && pastedText[i] === "\n" && pastedText[i + 1] !== "\n") {
				pastedText = `${pastedText.slice(0, i + 1)}${guids[guidIndex++]}\t${pastedText.slice(i + 1)}`;
			}
		}

		return pastedText;
	}

	// Used when an excel/sheet table of xyicons/boundaries/markups are getting pasted
	public static formatTableString(tableString: string) {
		const separator = "\t";
		tableString = StringUtils.removeTrailingCharacter(
			tableString.replace(/\r\n/g, "\n"), // replaces \r\n newlines (windows-style) with unix style new lines (\n)
			"\n",
		);

		const blockStarters = [tableString.indexOf("Xyicon Model"), tableString.indexOf("Boundary Type"), tableString.indexOf("Markup Type")];

		const blocks: string[] = [];

		if (blockStarters[0] > -1) {
			let blockEndIndex = blockStarters[1] === -1 ? blockStarters[2] : blockStarters[1];

			blockEndIndex = blockEndIndex === -1 ? undefined : blockEndIndex;
			blocks.push(tableString.substring(blockStarters[0], blockEndIndex));
		}

		if (blockStarters[1] > -1) {
			let blockEndIndex = blockStarters[2];

			blockEndIndex = blockEndIndex === -1 ? undefined : blockEndIndex;
			blocks.push(tableString.substring(blockStarters[1], blockEndIndex));
		}

		if (blockStarters[2] > -1) {
			blocks.push(tableString.substring(blockStarters[2]));
		}

		// Terminology: one "block" contains one type of spaceitems: xyicons, boundaries, or markups
		const formattedBlocks: string[] = [];

		for (const block of blocks) {
			formattedBlocks.push(this.formatBlockOfString(block, separator));
		}

		return formattedBlocks.join("\n\n");
	}

	/**
	 * Replace \n with <br/> in multiline fields.
	 * The idea is that we count the separators within the headers, and if the new line character (\n) comes sooner than the last separator on the specific line,
	 * we assume it's a multiline field
	 * @param tableString
	 * @param separator "\t", ",", etc
	 */
	private static formatBlockOfString(tableString: string, separator: string) {
		let formattedString = StringUtils.removeTrailingCharacter(tableString, "\n");

		let firstLineOfBlock: string = "";
		let separatorSumInHeader: number = 0;
		let separatorCounter: number = 0;
		let lineCounter: number = 0;

		const newLineReplacement = "<br/>";

		for (let i = 0; i < formattedString.length - 1; ++i) {
			if (i === 0) {
				firstLineOfBlock = formattedString.split("\n")[0];
				separatorSumInHeader = firstLineOfBlock.split(separator).length - 1;
			}

			if (formattedString[i] === separator) {
				separatorCounter++;
			} else if (formattedString[i] === "\n") {
				if (separatorCounter < separatorSumInHeader) {
					formattedString = `${formattedString.slice(0, i)}${newLineReplacement}${formattedString.slice(i + 1)}`;

					i += newLineReplacement.length - 1;
				} else {
					lineCounter++;
					if (formattedString[i + 1] === "\n") {
						const indexOfNextNewLine = formattedString.indexOf("\n", i + 2);

						// eslint-disable-next-line sonarjs/updated-loop-counter
						i = Math.max(indexOfNextNewLine, i);
						separatorSumInHeader = formattedString.substring(i, indexOfNextNewLine).split(separator).length - 1;
					} else {
						separatorCounter = 0;
					}
				}
			}
		}

		return formattedString;
	}

	private static removeTrailingCharacter(value: string, character: string): string {
		let endIndex = value.length;

		while (endIndex > 0 && value[endIndex - 1] === character) {
			endIndex--;
		}

		return value.slice(0, endIndex);
	}

	public static removeWhiteSpaces(text: string) {
		return text.trim().replace(/\s+/g, " ");
	}

	/**
	 * From:
	 * https://stackoverflow.com/questions/36288375/how-to-parse-csv-data-that-contains-newlines-in-field-using-javascript
	 *
	 * CSVToArray parses any String of Data including '\r' '\n' characters,
	 * and returns an array with the rows of data.
	 * @param {String} CSV_string - the CSV string you need to parse
	 * @param {String} delimiter - the delimeter used to separate fields of data
	 * @returns {Array} rows - rows of CSV where first row are column headers
	 */
	public static CSVToArray(CSV_string: string, delimiter: string = ",", maxColumns = Infinity): (string | number)[][] {
		try {
			delimiter = delimiter || ","; // user-supplied delimeter or default comma

			const pattern = new RegExp(
				// regular expression to parse the CSV values.
				// Delimiters:
				`(\\${delimiter}|\\r?\\n|\\r|^)` +
					// Quoted fields: "field",
					// it can possible be prefixed with an equal sign: \=?
					'(?:=?"([^"]*(?:""[^"]*)*)"|' +
					// Standard fields.
					`([^"\\${delimiter}\\r\\n]*))`,
				"gi",
			);

			const rows: (string | number)[][] = [[]]; // array to hold our data. First row is column headers.
			// array to hold our individual pattern matching groups:
			let matches: string[] | false = false; // false if we don't find any matches

			// Loop until we no longer find a regular expression match
			while ((matches = pattern.exec(CSV_string))) {
				const matched_delimiter = matches[1]; // Get the matched delimiter

				// Check if the delimiter has a length (and is not the start of string)
				// and if it matches field delimiter. If not, it is a row delimiter.
				if (matched_delimiter.length && matched_delimiter !== delimiter) {
					// Since this is a new row of data, add an empty row to the array.
					rows.push([]);
				}
				let matched_value;

				// Once we have eliminated the delimiter, check to see
				// what kind of value was captured (quoted or unquoted):
				if (matches[2] === "" || (matches[2] && matches[2].trim() === "")) {
					//Check whther its empty
					matched_value = "";
				} else if (matches[2]) {
					// found quoted value. unescape any double quotes.
					matched_value = matches[2].replace(new RegExp('""', "g"), '"');
				} else {
					//to catch if there an empty comming here and haddle it without returning 0
					if (matches[3] === "" || (matches[3] && matches[3].trim() === "")) {
						matched_value = "";
					} else if (matches[3]) {
						//found a quoted value for matches[3]
						matched_value = matches[3].replace(new RegExp('""', "g"), '"');
					}
				}
				// Now that we have our value string, let's add
				// it to the data array.

				const row = rows[rows.length - 1];

				if (row.length < maxColumns) {
					if (matched_value === "") {
						// Leave empty values as they are, don't convert them to numbers, because they'll become "0", and
						// according to #8586, we don't want that
					} else {
						const matchedValueAsNumber: number = Number(matched_value);

						if (!isNaN(matchedValueAsNumber)) {
							matched_value = matchedValueAsNumber;
						}
					}

					row.push(matched_value);
				}
			}
			return rows; // Return the parsed data Array
		} catch (e) {
			console.warn(e);
			return [];
		}
	}
}
